/**
 * The food log's date layer: Hebrew names, and a month laid out as calendar cells.
 *
 * ── two rules, both learned the hard way ──
 *
 * **Never `new Date('2026-08-07')`.** That is UTC midnight, which renders as the 6th for anyone
 * behind UTC — a day-number-off-by-one that only appears for some readers, in some months. Every
 * `YYYY-MM-DD` here is split on the string, or parsed with `moment(str, KEY)`, which parses local.
 * (This rule moved here from `WeekDayTabs`, which the month grid replaced.)
 *
 * **Never `moment().format('MMMM')` for a month name.** `moment.locale()` is a GLOBAL, set to `he`
 * at module scope by half a dozen unrelated files (`MessageInboxDrawer`, `AuditLogPage`,
 * `NewMessageDialog`, …). Whether this screen says `ספטמבר` or `September` would depend on whether
 * one of those modules happened to be imported first — and in a Jest test rendering the food log
 * alone, none of them is, so the locale is `en`. Literal arrays are the only stable answer, and the
 * coach app reached the same conclusion for the same reason.
 */

import moment from "moment";

export const KEY = "YYYY-MM-DD";

export const HE_MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

/** Sunday-first, matching the order a week is laid out in. */
export const HE_WEEKDAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

/** The column headers. Geresh U+05F3, not an ASCII apostrophe — as everywhere else in the app. */
export const HE_DAY_LETTERS = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

/** `"2026-09-14"` → `{ year: 2026, month: 9, day: 14 }`. Split, not parsed — see the header. */
export const parts = (date: string) => {
  const [year, month, day] = date.split("-");
  return { year: Number(year), month: Number(month), day: Number(day) };
};

/** `"2026-09-14"` → `"2026-09-01"` */
export const monthStartOf = (date: string): string => `${date.slice(0, 7)}-01`;

/** `"2026-09-01"` → `"ספטמבר 2026"` */
export const monthLabel = (monthStart: string): string => {
  const { year, month } = parts(monthStart);
  return `${HE_MONTHS[month - 1]} ${year}`;
};

/** `"2026-09-04"` → `"יום שישי, 4 בספטמבר"` */
export const dayHeading = (date: string): string => {
  const { month, day } = parts(date);
  const weekday = HE_WEEKDAYS[moment(date, KEY).day()];
  return `יום ${weekday}, ${day} ב${HE_MONTHS[month - 1]}`;
};

/** `"2026-09-04"` → `"4/9"` */
export const shortDate = (date: string): string => {
  const { month, day } = parts(date);
  return `${day}/${month}`;
};

export const addMonths = (monthStart: string, count: number): string =>
  moment(monthStart, KEY).clone().add(count, "month").format(KEY);

/**
 * The month as a grid of cells: leading `null`s so the 1st lands under its own weekday, then one
 * `YYYY-MM-DD` per day. Sunday-first, matching `HE_DAY_LETTERS`.
 *
 * `moment(monthStart, KEY)` rather than `new Date(monthStart)` — see the header. `.day()` is 0 for
 * Sunday, which is exactly the number of blanks needed.
 */
export const monthCells = (monthStart: string): Array<string | null> => {
  const first = moment(monthStart, KEY);
  const blanks: Array<string | null> = Array(first.day()).fill(null);
  const days = Array.from({ length: first.daysInMonth() }, (_, i) =>
    first.clone().add(i, "day").format(KEY)
  );
  return [...blanks, ...days];
};
